"use client";

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { PasswordInput } from "@/components/password-input";
import { AuthHeader } from "@/components/auth-header";
import { AppleSignInButton } from "@/components/apple-signin-button";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Feedback } from "@/components/feedback";
import { useTranslations } from "next-intl";
import { useInviteRedirect } from "@/hooks/useInviteRedirect";
import {
    DEV_EMAIL,
    DEV_PASSWORD,
    ENV_DEVELOPMENT,
    PASSWORD_MIN_LENGTH,
} from "@/lib/constants";
import { usePageTitle } from "@/hooks/usePageTitle";
import { getHomeRoute } from "@/lib/roles";
import { safeReturnUrl } from "@/lib/safe-return-url";
import { PhoneIcon, SignInIcon } from "@phosphor-icons/react/dist/ssr";
import { AuthHoneypotField } from "@/components/auth-honeypot-field";
import {
    honeypotRejectDelayMs,
    isHoneypotTripped,
} from "@/lib/honeypot";

export default function EmailLogin() {
    const { login, loginWithApple } = useAuth();
    const router = useRouter();
    const searchParams = useSearchParams();
    const t = useTranslations();

    usePageTitle("pageTitles.signIn");

    const [email, setEmail] = useState(
        ENV_DEVELOPMENT && DEV_EMAIL ? DEV_EMAIL : ""
    );
    const [password, setPassword] = useState(
        ENV_DEVELOPMENT && DEV_PASSWORD ? DEV_PASSWORD : ""
    );
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [honeypot, setHoneypot] = useState("");

    useInviteRedirect();

    // Get return URL from search params
    const returnUrl = searchParams.get("returnUrl");

    const handleAppleSuccess = async (idToken: string) => {
        setError("");
        setIsSubmitting(true);

        try {
            const user = await loginWithApple(idToken);
            const redirectPath = safeReturnUrl(returnUrl) ?? getHomeRoute(user);
            router.push(redirectPath);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : t("auth.appleLoginFailed"),
            );
            setIsSubmitting(false);
        }
    };

    const handleAppleError = () => {
        setError(t("auth.appleLoginFailed"));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setIsSubmitting(true);

        if (isHoneypotTripped(honeypot)) {
            await new Promise((r) => setTimeout(r, honeypotRejectDelayMs()));
            setError(t("auth.loginFailed"));
            setIsSubmitting(false);
            return;
        }

        try {
            const user = await login(email, password);
            // Redirect to return URL if provided, otherwise go to the user's
            // role-aware home route (restricted roles never land on /dashboard)
            const redirectPath = safeReturnUrl(returnUrl) ?? getHomeRoute(user);
            router.push(redirectPath);
        } catch (err) {
            setError(
                err instanceof Error ? err.message : t("auth.loginFailed")
            );
            setIsSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="relative grid w-full grid-cols-1"
        >
            <AuthHoneypotField
                value={honeypot}
                onChange={setHoneypot}
            />
            <AuthHeader heading={t("auth.signInToAccount")} />

            {error && (
                <div className="mb-4">
                    <Feedback message={error} status="error" />
                </div>
            )}

            <div className="grid grid-cols-1 gap-8">
                <div className="space-y-2">
                    <Label>{t("common.email")}</Label>
                    <Input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </div>
                <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                        <Label>{t("common.password")}</Label>
                        <Link
                            href="/auth/password/forgot"
                            className="text-sm font-semibold text-primary hover:underline"
                        >
                            {t("auth.forgotPassword")}
                        </Link>
                    </div>
                    <PasswordInput
                        value={password}
                        onChange={setPassword}
                        name="password"
                        required
                        minLength={PASSWORD_MIN_LENGTH}
                        autoComplete="current-password"
                    />
                </div>
                <Button
                    type="submit"
                    variant="default"
                    className="w-full"
                    disabled={isSubmitting}
                >
                    <SignInIcon weight="bold" className="size-5" />
                    {isSubmitting ? t("auth.signingIn") : t("auth.signIn")}
                </Button>
                <p className="text-sm text-muted-foreground">
                    {t("auth.dontHaveAccount")}{" "}
                    <Link
                        href="/auth/signup"
                        className="font-semibold text-primary hover:underline"
                    >
                        {t("auth.signUp")}
                    </Link>
                </p>
                <div>
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="bg-card px-3 text-muted-foreground">
                                {t("auth.or")}
                            </span>
                        </div>
                    </div>
                    <div className="mt-8 space-y-3">
                        <AppleSignInButton
                            onSuccess={handleAppleSuccess}
                            onError={handleAppleError}
                            disabled={isSubmitting}
                        />
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={() => {
                                const redirectPath = returnUrl ? `/auth/signin/phone?returnUrl=${returnUrl}` : "/auth/signin/phone";
                                router.push(redirectPath);
                            }}
                        >
                            <PhoneIcon className="size-5" />
                            {t("auth.loginWithPhone")}
                        </Button>
                    </div>
                </div>
            </div>
        </form>
    );
}
